import { useAtomValue } from 'jotai';
import { get, set } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  KpiConfigPeriod,
  KpiPeriod,
  KpiPeriodExtended,
  KpiSection,
} from '../../../../../data-models/company-financials.data-model';
import { IField } from '../../../../../data-models/field2.data-model';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import {
  kpiConfigAtom,
  kpiConfigByIdMapAtom,
  textualKpisByIdAtom,
} from '../../../../../services/state/KPIConfigState';
import { financialsDisplayKpisAtom } from './FinancialsKpiDisplayState';
import { dateToPlainDateString } from '../../../../../util/date-utilts';
import { fiscalDateFormatter2 } from '../../../../../util/formatters/DateFormatters';
import { captureUnexpectedState } from '../../../../../util/SentryUtils';
import { allCompanyFinancialsState } from '../../../state/CompanyFinancialsState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { getEachDateByPeriodAndFrequency } from '../../../utils/financialUtils';
import { getTypedFinancials, TypedFinancials } from '../../../utils/getTypedFinancials';
import {
  selectedEndDateFinancialsState,
  selectedFinancialsRangeState,
  selectedFrequencyFinancialsState,
} from '../../FinancialsDatePickers/useFinancialsDateSettings';
import { FinancialsDisplaySettings } from '../components/FinancialsSettingsState';
import { KpiType } from '../FinancialsTypeToggle';
export type FinancialType = KpiSection | KpiPeriodExtended | 'growth';

export interface IFinancialsRowData2 extends IField<unknown> {
  valuesByDate2: Record<string, Record<string, Record<KpiSection, TypedFinancials>>>;
}

export function useFinancialsGridData() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const companyFinancials = useAtomValue(allCompanyFinancialsState(companyId));
  const kpiConfigMap = useAtomValue(kpiConfigByIdMapAtom);
  const typedFinancials = getTypedFinancials(companyFinancials, kpiConfigMap);
  const allKpis = useAtomValue(kpiConfigAtom);
  const textualKpisById = useAtomValue(textualKpisByIdAtom);
  const displayKpis = useAtomValue(financialsDisplayKpisAtom);
  const displayKpiIds = useMemo(() => new Set(displayKpis.map((k) => k.id)), [displayKpis]);

  const getFinancialsRowData = useCallback(
    ({
      period,
      displaySettings = {},
      type,
    }: {
      period: KpiPeriod;
      displaySettings?: Partial<FinancialsDisplaySettings>;
      type?: KpiType;
    }): IFinancialsRowData2[] => {
      const filterFn = displaySettingsToFinancialsFilter({
        displaySettings,
        selectedPeriod: period,
        type,
        textualKpisById,
      });
      const dataMap2 = typedFinancials.filter(filterFn).reduce(
        (acc, item) => {
          return set(acc, [item.kpiId, item.date, item.period!, item.section!], item);
        },
        {} as Record<number, IFinancialsRowData2['valuesByDate2']>
      );

      return allKpis
        .filter((kpi) => {
          if (!textualKpisById.has(kpi.id) && !displayKpiIds.has(kpi.id)) {
            return false;
          }
          // If show reportedOnly check if there is no data for the KPI
          return (
            (!displaySettings.reportedOnly && passesTypeFilter(kpi.id, textualKpisById, type)) ||
            kpi.id in dataMap2
          );
        })
        .map((kpi) => {
          return { ...kpi, valuesByDate2: dataMap2[kpi.id] };
        });
    },
    [allKpis, textualKpisById, displayKpiIds, typedFinancials]
  );

  const getFinancialsByDate = useCallback(
    ({
      period,
      displaySettings = {},
      type,
    }: {
      period: KpiPeriod;
      displaySettings?: Partial<FinancialsDisplaySettings>;
      type?: 'quantitative' | 'qualitative';
    }): Record<string, Record<number, Record<KpiPeriod, Record<KpiSection, TypedFinancials>>>> => {
      const filterFn = displaySettingsToFinancialsFilter({
        displaySettings,
        selectedPeriod: period,
        type,
        textualKpisById,
      });
      const result = typedFinancials.filter(filterFn).reduce(
        (acc, item) => {
          return set(acc, [item.date, item.kpiId, item.period!, item.section!], item);
        },
        {} as Record<string, Record<number, Record<KpiPeriod, Record<KpiSection, TypedFinancials>>>>
      );
      return result;
    },
    [textualKpisById, typedFinancials]
  );

  return { getFinancialsRowData, getFinancialsByDate };
}

export function useAllSelectedPeriods() {
  const selectedPeriodRange = useAtomValue(selectedFinancialsRangeState);
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const fye = useAtomValue(companyStateJ(companyId))?.fye ?? 12;
  const reportingFrequency = useAtomValue(selectedFrequencyFinancialsState);
  const selectedEndDate = useAtomValue(selectedEndDateFinancialsState);

  return useCallback(() => {
    if (!selectedEndDate || !reportingFrequency || !selectedPeriodRange || selectedPeriodRange < 0) {
      captureUnexpectedState('useAllSelectedPeriods', {
        selectedEndDate,
        reportingFrequency,
        selectedPeriodRange,
      });
      return new Map<string, string>();
    }
    const allDates = getEachDateByPeriodAndFrequency({
      totalSelectedPeriods: selectedPeriodRange,
      frequency: reportingFrequency,
      fye,
      endDate: selectedEndDate,
    });

    return allDates.dates.reverse().reduce((isoDateToFormattedDate, date) => {
      return isoDateToFormattedDate.set(
        dateToPlainDateString(date),
        fiscalDateFormatter2({
          period: reportingFrequency,
          periodISODate: dateToPlainDateString(date),
          fye,
        })
      );
    }, new Map<string, string>());
  }, [selectedEndDate, fye, reportingFrequency, selectedPeriodRange]);
}

export function useTextualKpiData() {
  const getAllSelectedPeriods = useAllSelectedPeriods();
  const { getFinancialsByDate } = useFinancialsGridData();
  const selectedFrequency = useAtomValue(selectedFrequencyFinancialsState);
  const kpisById = useAtomValue(kpiConfigByIdMapAtom);

  return useMemo(() => {
    if (!selectedFrequency) {
      captureUnexpectedState('useTextualKpiData', {
        selectedFrequency,
      });
      return null;
    }
    const allPeriods = getAllSelectedPeriods();
    const allFinancialsByDate = getFinancialsByDate({
      period: selectedFrequency,
      displaySettings: {},
      type: 'qualitative',
    });
    const textualKpisByDate = [...allPeriods.keys()].reduce((map, date) => {
      map.set(String(date), new Map<string, string>());

      const kpiIdsToValuesByPeriod = get(allFinancialsByDate, [date]);
      if (!kpiIdsToValuesByPeriod) {
        return map;
      }
      const sortedKpis = Object.keys(kpiIdsToValuesByPeriod).sort((a, b) => {
        return (kpisById.get(Number(a))?.displayName ?? '').localeCompare(
          kpisById.get(Number(b))?.displayName ?? ''
        );
      });
      sortedKpis.forEach((kpiId) => {
        const kpiName = kpisById.get(Number(kpiId))?.displayName;
        if (!kpiName) {
          return;
        }
        // we are showing only actual for now
        const valueForPeriod = get(kpiIdsToValuesByPeriod, [
          kpiId,
          selectedFrequency,
          KpiSection.actual,
          'value',
        ]);
        if (valueForPeriod != null) {
          map.get(String(date))!.set(kpiName, valueForPeriod);
        }
      });

      return map;
    }, new Map<string, Map<string, string>>());

    return { allPeriods, textualKpisByDate };
  }, [getAllSelectedPeriods, getFinancialsByDate, kpisById, selectedFrequency]);
}

function displaySettingsToFinancialsFilter({
  displaySettings,
  selectedPeriod,
  type,
  textualKpisById,
}: {
  displaySettings: Partial<FinancialsDisplaySettings>;
  selectedPeriod: KpiPeriod;
  type?: 'quantitative' | 'qualitative';
  textualKpisById: Map<number, IField<unknown>>;
}): (item: TypedFinancials) => boolean {
  const includedPeriods = new Set<KpiPeriod | KpiPeriodExtended>([selectedPeriod]);
  if (displaySettings.showPinnedYears) {
    includedPeriods.add(KpiPeriod.year);
  }
  if (displaySettings.showYTD) {
    includedPeriods.add(KpiConfigPeriod.CYTD);
  }
  if (displaySettings.showLTM) {
    includedPeriods.add(KpiConfigPeriod.LTM);
  }
  if (displaySettings.showGrowth) {
    includedPeriods.add(`Growth${selectedPeriod}`);
    if (displaySettings.showPinnedYears) {
      includedPeriods.add(`Growth${KpiPeriod.year}`);
    }
  }

  const standardPeriods = new Set(Object.values(KpiPeriod));
  return function isFinancialIncluded(financials: TypedFinancials) {
    if (!financials.section || !financials.period) {
      return false;
    }
    if (!includedPeriods.has(financials.period)) {
      return false;
    }
    if (!displaySettings.showBudget && financials.section === KpiSection.budget) {
      return false;
    }
    if (!standardPeriods.has(financials.period as KpiPeriod) && financials.section !== KpiSection.actual) {
      return false;
    }
    if (!passesTypeFilter(financials.kpiId, textualKpisById, type)) {
      return false;
    }
    return true;
  };
}

function passesTypeFilter(
  kpiId: number,
  textualKpisById: Map<number, IField<unknown>>,
  type?: 'quantitative' | 'qualitative'
) {
  if (!type) {
    return true;
  }
  if (type === 'quantitative' && !textualKpisById.has(kpiId)) {
    return true;
  }
  if (type === 'qualitative' && textualKpisById.has(kpiId)) {
    return true;
  }
  return false;
}
